import { boxShadow } from '@/theme/foundations/box-shadow';
import { THEME_ATTRIBUTE } from './colorMode.constants';
import {
  DARK_BORDER,
  DARK_PAGE,
  DARK_SURFACE,
  buildDarkCssVariables,
  hexToChannels,
} from './darkPalette';

const DARK = `html[${THEME_ATTRIBUTE}="dark"]`;

// Rings that mark a selected/highlighted element keep showing in dark.
const KEPT_SHADOWS = new Set(['light-highlight']);

/**
 * The theme's own elevation shadows, switched off in dark mode: they are light
 * grey glows meant for a white page, and on a dark surface they read as a
 * smudge. Chakra flattens nested tokens with `-` (`light.primary` →
 * `--kula-shadows-light-primary`). Chakra's built-in scale (focus `outline`,
 * menu/popover `md`/`lg`) is left alone.
 */
export const buildDarkShadowVariables = (): Record<string, string> => {
  const vars: Record<string, string> = { '--kula-shadows-primary': 'none' };
  Object.entries(boxShadow).forEach(([key, value]) => {
    const names =
      typeof value === 'string'
        ? [key]
        : Object.keys(value).map((child) => `${key}-${child}`);
    names
      .filter((name) => !KEPT_SHADOWS.has(name))
      .forEach((name) => {
        vars[`--kula-shadows-${name}`] = 'none';
      });
  });
  return vars;
};

/**
 * A selector list split on its top-level commas only: a comma inside
 * `:not(...)` or a quoted attribute value (`[style*="rgb(255, 255, 255)"]`)
 * belongs to that selector.
 */
export const splitSelectorList = (list: string): string[] => {
  const parts: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let i = 0; i < list.length; i += 1) {
    const char = list[i];
    if (quote) {
      if (char === quote) quote = null;
    } else if (char === '"' || char === "'") {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth += 1;
    } else if (char === ')' || char === ']') {
      depth -= 1;
    } else if (char === ',' && depth === 0) {
      parts.push(list.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(list.slice(start));
  return parts;
};

/** Every selector below is scoped under the dark attribute on `<html>`. */
const scoped = (selector: string) =>
  splitSelectorList(selector)
    .map((part) => `${DARK} ${part.trim()}`)
    .join(', ');

/**
 * The stroke around floating surfaces (modals, drawers, menus, popovers,
 * select menus). With no shadows in dark mode, a surface sitting on another
 * dark surface needs a visible edge; the regular border (secondary-100) is
 * too close to the surface to read as one.
 */
export const DARK_FLOATING_BORDER = 'var(--kula-colors-secondary-300)';

// Highcharts writes a label's colour into its inline style, normalised to
// `rgb(…)` (a chart's '#FFFFFF' reads `rgb(255, 255, 255)`); the hex forms
// cover labels styled outside Highcharts. No `;` in these: the CSS-in-JS
// parser splits a selector on it and drops the whole rule.
const NOT_WHITE = ['rgb(255, 255, 255)', '#FFFFFF', '#ffffff', 'color: white']
  .map((white) => `:not([style*="${white}"])`)
  .join('');

const surface = (alpha?: number) =>
  alpha === undefined
    ? DARK_SURFACE
    : `rgb(${hexToChannels(DARK_SURFACE)} / ${alpha})`;

/**
 * react-select (v4) is styled in ~200 places through JS `styles` objects with
 * light hex values, which no variable reaches. Every element it renders
 * carries an Emotion class labelled with its part (`css-1a2b3c-control`,
 * `-menu`, `-option`, `-multiValue`…), so dark mode re-skins it here once,
 * GitHub-style: a dark input fill, a bordered dark menu, grey hover and focus
 * rows. `html[…] [class*=…][class*=…]` (0,3,1) out-ranks the Emotion class
 * (0,1,0) without `!important`.
 */
// Exact part match: the label ends the class list or is followed by a space.
// A plain substring would also hit Chakra's `chakra-form-control` (every
// form field wrapper) or `-menuList` for `-menu`.
const rs = (part: string, extra = '') =>
  [`[class$="-${part}"]`, `[class*="-${part} "]`]
    .map((match) => `[class*="css-"]${match}:not(.chakra-form-control)${extra}`)
    .join(', ');

export const buildReactSelectDarkStyles = () => ({
  [scoped(rs('control'))]: {
    backgroundColor: 'var(--kula-colors-input-fill)',
    // !important: some selects set their border colour with !important.
    borderColor: 'var(--kula-colors-secondary-200) !important',
    color: 'var(--kula-colors-secondary-900)',
    boxShadow: 'none',
  },
  [scoped(rs('control', ':hover'))]: {
    borderColor: 'var(--kula-colors-secondary-300) !important',
  },
  [scoped(rs('control', ':focus-within'))]: {
    borderColor: 'var(--kula-colors-primary-500) !important',
  },
  [scoped(`${rs('singleValue')}, ${rs('input', ' input')}`)]: {
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped(rs('placeholder'))]: {
    color: 'var(--kula-colors-secondary-500)',
  },
  [scoped(rs('menu'))]: {
    backgroundColor: 'var(--kula-colors-white)',
    border: `1px solid ${DARK_FLOATING_BORDER}`,
    color: 'var(--kula-colors-secondary-900)',
  },
  // The menu list, "no options" and loading messages carry no label of
  // their own; they are the menu's direct children.
  [scoped(rs('menu', ' > div'))]: {
    backgroundColor: 'transparent',
  },
  [scoped(rs('option'))]: {
    backgroundColor: 'transparent',
    color: 'var(--kula-colors-secondary-900)',
    // Row rules (kula Select and others draw them with light hex literals).
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  // v4 adds state classes only with a `classNamePrefix`; hover covers the rest.
  [scoped(`${rs('option', ':hover')}, [class*="__option--is-focused"]`)]: {
    backgroundColor: 'var(--kula-colors-secondary-50)',
  },
  [scoped('[class*="__option--is-selected"]')]: {
    backgroundColor: 'var(--kula-colors-primary-50)',
    color: 'var(--kula-colors-primary-900)',
  },
  [scoped(rs('multiValue'))]: {
    backgroundColor: 'var(--kula-colors-secondary-100)',
    borderColor: 'var(--kula-colors-secondary-200)',
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped(rs('multiValue', ' > div'))]: {
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped(rs('multiValue', ' > div:last-of-type:hover'))]: {
    backgroundColor: 'var(--kula-colors-secondary-200)',
    color: 'var(--kula-colors-secondary-950)',
  },
  [scoped(rs('indicatorSeparator'))]: {
    backgroundColor: 'var(--kula-colors-secondary-200)',
  },
  [scoped(rs('indicatorContainer'))]: {
    color: 'var(--kula-colors-secondary-600)',
  },
  // Group headings ("They rejected us"...) have no label of their own; they
  // are the group's first child, often given a white background.
  [scoped(rs('group', ' > div:first-of-type'))]: {
    backgroundColor: 'transparent !important',
    color: 'var(--kula-colors-secondary-600) !important',
  },
  // Group headings are labelled `group` too (emotion reuses the label), and
  // some selects rule them with light hex literals.
  [scoped(`${rs('group')}, ${rs('group', ' > div:first-of-type')}`)]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
});

/**
 * The kula DatePicker (react-date-range) calendar. @kula-ai/components styles
 * it with light literals, most `!important`: month days in dark navy
 * (#413b74, unreadable on the dark surface) while the other months' days and
 * disabled days get a pale grey (#c4c5e0, which reads brighter than the
 * month itself), and a #edf0ff circle/band for today, hover and the selected
 * range. Here: month days light, other/disabled days dim, the circles and
 * bands a dark primary tint. Selected days keep their accent fill and white
 * number.
 */
export const buildDatePickerDarkStyles = () => ({
  [scoped('.rdrCalendarWrapper.rdrCalendarWrapper')]: {
    color: 'var(--kula-colors-secondary-900)',
  },
  [scoped('.rdrWeekDay.rdrWeekDay')]: {
    color: 'var(--kula-colors-secondary-600)',
  },
  [scoped('.rdrDayNumber span')]: {
    color: 'var(--kula-colors-secondary-900) !important',
  },
  [scoped(
    '.rdrDayPassive .rdrDayNumber span, .rdrDayDisabled .rdrDayNumber span'
  )]: {
    color: 'var(--kula-colors-secondary-400) !important',
  },
  // kula's selected-day white number (0,3,2) and in-range accent (0,3,2)
  // must still beat the month-day rule above.
  [scoped(
    '.rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span'
  )]: {
    color: 'var(--kula-colors-static-white) !important',
  },
  [scoped('.rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span')]: {
    color: 'var(--kula-colors-primary-700) !important',
  },
  [scoped(
    '.rdrDay:not(.rdrDayPassive).rdrDayToday .rdrDayNumber:after, .rdrDay:not(.rdrDayPassive):not(.rdrDayDisabled):hover .rdrDayNumber:after, .rdrCalendarWrapper:not(.rdrDateRangeWrapper) .rdrDay:not(.rdrDayPassive):not(.rdrDayDisabled).rdrDayHovered .rdrDayNumber:after'
  )]: {
    background: 'var(--kula-colors-primary-100) !important',
  },
  [scoped(
    '.rdrInRange.rdrInRange, .rdrDay:has(.rdrStartEdge):not(:has(.rdrEndEdge)):before, .rdrDay:has(.rdrEndEdge):not(:has(.rdrStartEdge)):before, .rdrDayStartPreview.rdrDayStartPreview, .rdrDayEndPreview.rdrDayEndPreview, .rdrDayInPreview.rdrDayInPreview'
  )]: {
    background: 'var(--kula-colors-primary-50) !important',
  },
  [scoped('.rdrNextPrevButton.rdrNextPrevButton:hover')]: {
    background: 'var(--kula-colors-secondary-50) !important',
  },
});

/**
 * Global styles for dark mode, merged into the Chakra theme's
 * `styles.global`. Inert until `<html data-kula-theme="dark">` is set.
 *
 * `html[…]` (0,1,1) out-ranks Chakra's own `:root, [data-theme]` variable
 * block (0,1,0) regardless of insertion order. The second selector covers any
 * element that carries Chakra's `data-theme` attribute, which re-declares the
 * light variables for its subtree.
 */
export const buildDarkModeStyles = () => ({
  [`${DARK}, ${DARK} [data-theme]`]: {
    ...buildDarkCssVariables(),
    ...buildDarkShadowVariables(),
    // The AI gradient button/spinner blend with `multiply`, which on a dark
    // page multiplies them to black. Read as `var(--kula-ai-blend, multiply)`.
    '--kula-ai-blend': 'normal',
    // Translucent loading / disabled overlays: light literals read as
    // `var(--kula-dark-scrim, rgba(246, 247, 247, 0.8))`; dark dims instead.
    '--kula-dark-scrim': 'rgb(20 19 30 / 0.72)',
    // Light pastel fills (`pastelBg`) show at 10% in dark (design).
    '--kula-dark-pastel-alpha': '0.1',
    // A 10% primary wash (design), for tinted header strips and pills that
    // read too strong as the solid dark primary-50. See `primaryWash`.
    '--kula-dark-primary-wash': 'rgb(125 120 255 / 0.1)',
    // Ask Kula's "why this matched" box: its light lavender wash (#F0E7FE at
    // 27%) at 60% of that strength (design).
    '--kula-dark-ai-reason-bg': 'rgb(240 231 254 / 0.16)',
    // The page loader's white disc behind the logo (design/loader): none in
    // dark, only the spinning ring shows.
    '--kula-dark-loader-disc': 'transparent',
    // kula's primary Button fill (`bg-primary-default` and its hover/active)
    // keeps the light brand gradient, as the Chakra primary button does: the
    // dark primary-500/600 ramp is a light purple under light text.
    '--kula-theme-gradient-primary-default':
      'linear-gradient(180deg, #605AFB 0%, #503BF0 100%)',
    '--kula-theme-gradient-primary-hover':
      'linear-gradient(180deg, #503BF0 0%, #442ED4 100%)',
    '--kula-theme-gradient-primary-active':
      'linear-gradient(180deg, #503BF0 0%, #605AFB 100%)',
    // !important: Chakra's ColorModeProvider writes an inline
    // `color-scheme: light` on <html> on every mount.
    colorScheme: 'dark !important',
  },

  // Page vs side nav (design): the main screen takes the darker page colour
  // and the side nav the surface one. The nav is drawn with `white` (the
  // active row, its notch corners, the strip behind the panels) and
  // `secondary-25` (the panels), so inside the nav the two swap: the active
  // row still joins the main screen, now in the page colour.
  [scoped('body')]: {
    backgroundColor: `${DARK_PAGE} !important`,
  },
  [scoped('[data-side-nav]')]: {
    '--kula-colors-white': DARK_PAGE,
    '--kula-colors-secondary-25': DARK_SURFACE,
  },

  // @kula-ai/components and our own Tailwind build use Tailwind's literal
  // `white` for surfaces (55 kula components), which no variable reaches.
  // Upstream fix: kula should use `bg-base-white` (themeable) for surfaces.
  [scoped(
    '.bg-white, .bg-base-white, .hover\\:bg-white:hover, .focus-within\\:bg-white:focus-within, .group:hover .group-hover\\:bg-white'
  )]: {
    backgroundColor: `${surface()} !important`,
  },
  // ...except a switch thumb (kula Switch draws it with `bg-white`): it
  // stays white in both modes (design), like the Chakra Switch's
  // `static-white` thumb.
  [scoped('[role="switch"] .bg-white, [role="switch"] .bg-base-white')]: {
    backgroundColor: 'var(--kula-colors-static-white, #FFFFFF) !important',
  },
  // A disabled switch dims its whole track (thumb included) to 40%, which on
  // the dark page turned the white thumb grey. Dark mode dims only the track.
  [scoped('.chakra-switch__track[data-disabled], [role="switch"]:disabled')]: {
    opacity: '1 !important',
    backgroundColor: 'var(--kula-colors-secondary-200) !important',
  },
  [scoped('.bg-white\\/90, .bg-white\\/85')]: {
    backgroundColor: `${surface(0.9)} !important`,
  },
  [scoped('.bg-white\\/70, .bg-white\\/65')]: {
    backgroundColor: `${surface(0.7)} !important`,
  },

  // Same gap for text: kula's rich-text editor sets Tailwind's literal
  // `text-black` on its content.
  [scoped('.text-black')]: {
    color: 'var(--kula-colors-secondary-950) !important',
  },

  // Third-party surfaces that hard-code white.
  [scoped('.simplebar-scrollbar::before')]: {
    background: '#67637F',
  },
  [scoped('.fc')]: {
    '--fc-page-bg-color': DARK_SURFACE,
    '--fc-neutral-bg-color': 'var(--kula-colors-secondary-50)',
    '--fc-border-color': DARK_BORDER,
    '--fc-today-bg-color': 'var(--kula-colors-primary-25)',
  },
  [scoped('.EZDrawer__container')]: {
    backgroundColor: `${surface()} !important`,
  },

  // Floating close button beside a drawer (a round chip holding only the X
  // icon, the drawer's first child): legacy drawers (conversational analytics
  // History, the limited candidate slider) give it an inline `#fff`.
  [scoped('.chakra-modal__content > div:first-child:has(> svg:only-child)')]: {
    backgroundColor: `${surface()} !important`,
    border: `1px solid ${DARK_FLOATING_BORDER}`,
  },

  // Circles list view (`ContactsTable.jsx`, a legacy file) paints every
  // header and cell with an inline `#fff` and rules them with `#EDF2F7`. The
  // cells take the page colour, as the other tables do in dark mode.
  [scoped(
    '.contacts-list-table, .contacts-list-table thead, .contacts-list-table th, .contacts-list-table td'
  )]: {
    backgroundColor: `${DARK_PAGE} !important`,
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  [scoped('.contacts-list-table tbody tr:hover td')]: {
    backgroundColor: 'var(--kula-colors-secondary-50) !important',
  },
  // ...and its rounded wrapper (inline `border: 1px solid #EDF2F7`, `white`
  // fill): no white frame, and no lighter strip past the last column.
  [scoped('div:has(> div > table.contacts-list-table)')]: {
    backgroundColor: `${DARK_PAGE} !important`,
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },

  // Settings side nav: the selected row's grey is a raw hex in index.css.
  [scoped('.settings-side-nav.active .nav')]: {
    background: 'var(--kula-colors-secondary-50)',
  },

  // The light theme's `p a` blue (#0043CE) is unreadable on the dark page.
  [scoped('p a')]: {
    color: 'var(--kula-colors-primary-700)',
  },

  ...buildReactSelectDarkStyles(),

  // Page-title icons (`KModuleHeader`) take a dark-purple stroke from
  // index.css; on the dark page they follow the title text instead.
  [scoped('.primary-900 path')]: {
    stroke: 'var(--kula-colors-secondary-900)',
  },

  // Highcharts writes its colours as SVG attributes and inline styles, so the
  // overrides need !important. Text follows the page, the plot area is
  // transparent and grid lines take the border colour.
  // HTML data labels (a format containing markup, e.g. the pipeline funnel):
  // Highcharts renders them as spans it colours black inline.
  // Labels a chart sets to white sit inside coloured bars or slices, where
  // white is what reads; only the default dark ones follow the page.
  [scoped(
    `.highcharts-data-label span${NOT_WHITE}, .highcharts-data-label span${NOT_WHITE} *`
  )]: {
    color: 'var(--kula-colors-secondary-700) !important',
  },
  [scoped('.highcharts-background, .highcharts-plot-background')]: {
    fill: 'transparent !important',
  },
  [scoped(
    `.highcharts-axis-labels text, .highcharts-axis-title, .highcharts-legend-item text, .highcharts-data-label text${NOT_WHITE}, .highcharts-title, .highcharts-subtitle, .highcharts-label text${NOT_WHITE}, .highcharts-stack-labels text${NOT_WHITE}`
  )]: {
    fill: 'var(--kula-colors-secondary-700) !important',
    color: 'var(--kula-colors-secondary-700) !important',
  },
  [scoped(
    '.highcharts-grid-line, .highcharts-axis-line, .highcharts-tick, .highcharts-minor-grid-line'
  )]: {
    stroke: 'var(--kula-colors-secondary-100) !important',
  },
  // Columns, slices and markers are outlined in white to separate them on a
  // white page; on the dark page the outline takes the surface colour.
  [scoped('.highcharts-point')]: {
    stroke: 'var(--kula-colors-white) !important',
  },
  // Label text gets a white outline (a stroked copy) for legibility on
  // white; on the dark page it smudges the text, so it takes the surface.
  [scoped('.highcharts-text-outline')]: {
    stroke: 'var(--kula-colors-white) !important',
    fill: 'var(--kula-colors-white) !important',
  },
  [scoped('.highcharts-tooltip-box')]: {
    fill: 'var(--kula-colors-secondary-50) !important',
    stroke: 'var(--kula-colors-secondary-200) !important',
  },
  [scoped('.highcharts-tooltip text, .highcharts-tooltip span')]: {
    fill: 'var(--kula-colors-secondary-900) !important',
    color: 'var(--kula-colors-secondary-900) !important',
  },

  // kula Select (`kula_custom_select` prefix) paints group headings as a
  // #F8F9FC band and rules options with #E9EBF4 literals inside the package.
  // Both need overriding here until the package reads theme variables.
  [scoped('[class*="kula_custom_select__group-heading"]')]: {
    backgroundColor: 'var(--kula-colors-secondary-50) !important',
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  [scoped('[class*="kula_custom_select__group-heading"] *')]: {
    color: 'var(--kula-colors-secondary-600) !important',
  },
  [scoped('[class*="kula_custom_select__option"]')]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },

  // kula uses `text-primary-25` as the light text on its brand fills
  // (primary Button, gradient Button, primary Tag). primary-25 is a dark tint
  // in dark mode, so on those fills the text keeps its light value.
  [scoped(
    '.text-primary-25.bg-primary-default:not(:disabled), .text-primary-25.bg-gradient-to-br:not(:disabled), .text-primary-25.bg-primary-400'
  )]: {
    color: '#F5F6FF !important',
  },
  [scoped('.bg-primary-400.text-primary-25')]: {
    backgroundColor: '#797DFF !important',
  },
  [scoped('.bg-gradient-to-br.from-primary-400.to-primary-700:not(:disabled)')]:
    {
      backgroundImage:
        'linear-gradient(to bottom right, #797DFF, #442ED4) !important',
    },

  // kula section-label chip (Schedule All "Interview plan", "Candidate
  // availability"): `bg-secondary-800 text-primary-25`, a dark purple chip
  // with light text. Both tokens invert in dark and turned it into a pale
  // chip; it keeps its light-mode colours instead.
  [scoped('.bg-secondary-800.text-primary-25')]: {
    backgroundColor: '#585281 !important',
    color: '#F5F6FF !important',
  },

  // kula Tabs `stepper` variant (Schedule All wizard header) is drawn with
  // hex literals: labels in #413B74 (active) / #6D659D (others), which are
  // dark purples on the dark page; the active number is white on the light
  // #797DFF disc (design: dark text on selection); the ring's moat is #FFFFFF.
  // `[class*=…]` also matches the hover variants (`group-hover:text-[…]`); the
  // #6D659D rule comes second so a label carrying both keeps the muted tone.
  [scoped('[role="tab"] [class*="text-[#413B74]"]')]: {
    color: 'var(--kula-colors-secondary-900) !important',
  },
  [scoped(
    '[role="tab"] [class*="text-[#6D659D]"], [role="tab"] [class*="text-[#9998BD]"]'
  )]: {
    color: 'var(--kula-colors-secondary-700) !important',
  },
  [scoped('[role="tab"]:hover [class*="group-hover:text-[#413B74]"]')]: {
    color: 'var(--kula-colors-secondary-900) !important',
  },
  [scoped('[role="tab"] [class*="border-[#9998BD]"]')]: {
    borderColor: 'var(--kula-colors-secondary-500) !important',
  },
  [scoped('[role="tab"] [class*="bg-[#797DFF]"]')]: {
    color: `${DARK_SURFACE} !important`,
  },
  [scoped('[role="tab"] svg [stroke="#FFFFFF"]')]: {
    stroke: DARK_SURFACE,
  },

  // Other light literals baked into @kula-ai/components classes.
  [scoped('.border-\\[\\#e9ebf4\\]')]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },
  [scoped('.bg-\\[\\#D7DAEB\\]')]: {
    backgroundColor: 'var(--kula-colors-secondary-200) !important',
  },

  // Skeleton shimmer: Chakra's skeleton reads these two variables (start
  // shown, pulsing toward end). Callers often pass light `startColor` /
  // `endColor` props, so the dark pair wins with !important.
  [scoped('.chakra-skeleton')]: {
    // Chakra's Skeleton keyframes read the unprefixed names; the theme's
    // baseStyle the `kula`-prefixed ones. Set both.
    '--skeleton-start-color': 'var(--kula-colors-secondary-50) !important',
    '--skeleton-end-color': 'var(--kula-colors-secondary-100) !important',
    '--kula-skeleton-start-color': 'var(--kula-colors-secondary-50) !important',
    '--kula-skeleton-end-color': 'var(--kula-colors-secondary-100) !important',
  },

  // react-content-loader shimmer (Circles cards and ~35 other loaders): an
  // SVG gradient `<id>-animated-diff` whose stops carry light colours as
  // attributes; CSS stop-color beats a presentation attribute.
  [scoped('linearGradient[id$="-animated-diff"] stop')]: {
    stopColor: 'var(--kula-colors-secondary-50) !important',
  },
  [scoped('linearGradient[id$="-animated-diff"] stop[offset="50%"]')]: {
    stopColor: 'var(--kula-colors-secondary-100) !important',
  },

  // react-date-range (report date filters): its stylesheet and ours rule the
  // months and the presets column with light literals. Structural borders
  // only; the day range/preview outlines keep their accent colour.
  [scoped(
    '.rdrMonth, .rdrDefinedRangesWrapper, .rdrStaticRange, .rdrDateDisplayWrapper, .rdrMonthAndYearWrapper, .rdrDateRangePickerWrapper'
  )]: {
    borderColor: 'var(--kula-colors-secondary-100) !important',
  },

  ...buildDatePickerDarkStyles(),

  // kula Select's floating (portalled) menu: FloatingMenuShell writes the
  // menu surface as an inline white hex from the package palette, on a
  // classless div; its own --rs-menu-max-h is the only stable hook.
  // Upstream gap: the package should read a theme variable.
  [scoped('div[style*="--rs-menu-max-h"]')]: {
    backgroundColor: `${surface()} !important`,
    borderColor: `${DARK_FLOATING_BORDER} !important`,
  },

  // Floating surfaces get a visible stroke (DARK_FLOATING_BORDER): Chakra
  // modals/drawers, menus and popovers, and the Radix-based ones from
  // @kula-ai/components (dialogs carry data-state; dropdowns, popovers and
  // selects render inside Radix's popper wrapper).
  [scoped(
    '.chakra-modal__content, .chakra-menu__menu-list, .chakra-popover__content:not([data-gradient-border]), [role="dialog"][data-state], [role="alertdialog"][data-state], [data-radix-popper-content-wrapper] > [data-state], .EZDrawer__container'
  )]: {
    border: `1px solid ${DARK_FLOATING_BORDER} !important`,
  },

  // kula's solid `bg-primary-500` fills (Switch track when on, primary
  // badges) keep the brand purple of the primary buttons; the dark ramp's
  // primary-500 is a lighter purple. Hover keeps kula's own hover fill.
  [scoped('.bg-primary-500:not(:hover)')]: {
    backgroundColor: 'var(--kula-colors-brand-fill) !important',
  },

  // The notifications panel slides out from the side nav, under the top
  // nav: no floating stroke on the edges that join them (left and top).
  [scoped('.EZDrawer__container.nav-docked-drawer')]: {
    borderLeft: 'none !important',
    borderTop: 'none !important',
  },

  // @kula-ai/assets illustrations (empty states, placeholders) are drawn
  // for the white page in light greys. Their gradients are all
  // `url(#kula-generic-…)`, so dark mode can find them and invert their
  // lightness (hue kept): pale paper becomes dark, like the recoloured
  // in-app illustrations (TasksEmpty).
  [scoped('svg:has([fill*="#kula-generic-"])')]: {
    filter: 'invert(0.86) hue-rotate(180deg)',
  },

  // Any other light illustration (an <img> or a non-kula SVG): a wrapper
  // marked data-dark-dim gets the same treatment as the kula assets above.
  [scoped('[data-dark-dim] svg, [data-dark-dim] img')]: {
    filter: 'invert(0.86) hue-rotate(180deg)',
  },

  // Decorative illustrations drawn in light greys for the white page (the
  // Ask Kula backdrop): dark mode fades them so they stay in the background.
  [scoped('[data-dark-fade]')]: {
    opacity: 0.35,
  },

  // Tailwind's stock green/red (not theme-aware) in @kula-ai/components,
  // e.g. the scorecard Overall evaluation: Strong yes / Yes green, No /
  // Strong no red. Same meaning as light mode, as dark tints.
  [scoped('.bg-green-50')]: {
    backgroundColor: 'var(--kula-colors-green-100) !important',
  },
  [scoped('.bg-red-50')]: {
    backgroundColor: 'var(--kula-colors-red-100) !important',
  },
  [scoped('.border-green-300')]: {
    borderColor: 'var(--kula-colors-green-700) !important',
  },
  [scoped('.border-red-300')]: {
    borderColor: 'var(--kula-colors-red-700) !important',
  },
  [scoped('.text-green-600, .text-green-700')]: {
    color: 'var(--kula-colors-green-700) !important',
  },
  [scoped('.text-red-600, .text-red-700')]: {
    color: 'var(--kula-colors-red-700) !important',
  },

  // Lavender AI wash (AI notetaker card): an arbitrary Tailwind rgba that
  // turns muddy light on a dark page; a dark purple tint instead.
  [scoped('[class*="bg-[rgba(240"]')]: {
    backgroundColor: 'var(--kula-colors-purple-50) !important',
  },

  // kula (Radix) tooltip content: a raised grey with light text, as the
  // Chakra tooltip theme does (theme/components/ToolTip.js).
  [scoped('.z-tooltip[data-state]')]: {
    backgroundColor: 'var(--kula-colors-secondary-200) !important',
    color: 'var(--kula-colors-secondary-950) !important',
    border: '1px solid var(--kula-colors-secondary-300)',
  },

  // No elevation shadows in dark mode: surfaces are separated by borders and
  // tone, the way dark UIs (GitHub's included) do it. Many shadows are
  // literals outside the theme tokens, so this covers them all. Keyboard focus
  // rings (box-shadow on :focus-visible) stay, and so does a shadow drawn as
  // a selection underline: mark that element `data-keep-shadow`.
  [scoped('*:not(:focus-visible):not([data-keep-shadow])')]: {
    boxShadow: 'none !important',
  },

  // Native controls (date pickers, scrollbars, autofill) follow the scheme.
  [scoped('input, textarea, select')]: {
    colorScheme: 'dark',
  },
  [scoped('::placeholder')]: {
    color: 'var(--kula-colors-secondary-500)',
  },
});
